import React, { useState, useEffect, useRef, memo } from 'react'
import { IoInformationCircleOutline, IoAlertCircleOutline } from 'react-icons/io5'
import type { ConfidenceLevelEnum } from '../../../types'

interface ConfidenceIndicatorProps {
  score: number
  level: ConfidenceLevelEnum
  compact?: boolean
  showLabel?: boolean
  showBar?: boolean
  flags?: string[]
  tooltipPosition?: 'top' | 'bottom'
  animated?: boolean
  size?: 'sm' | 'md' | 'lg'
}

function ConfidenceIndicatorBase({
  score,
  level,
  compact = false,
  showLabel = false,
  showBar = false,
  flags = [],
  tooltipPosition = 'bottom',
  animated = true,
  size = 'md',
}: ConfidenceIndicatorProps) {
  const [showTooltip, setShowTooltip] = useState(false)
  const [animatedWidth, setAnimatedWidth] = useState(0)
  const hasAnimated = useRef(false)

  // Normalize score (handle both 0-1 and 0-100 ranges)
  const normalizedScore = score > 1 ? score : score * 100

  // Animate bar fill on mount
  useEffect(() => {
    if (animated && showBar && !hasAnimated.current) {
      hasAnimated.current = true
      // Small delay for animation effect
      const timer = setTimeout(() => {
        setAnimatedWidth(normalizedScore)
      }, 100)
      return () => clearTimeout(timer)
    } else if (!animated) {
      setAnimatedWidth(normalizedScore)
    }
  }, [animated, showBar, normalizedScore])

  // Map level to number of filled dots (out of 5)
  // Support both snake_case from backend and legacy formats
  const levelToDots: Record<string, number> = {
    speculative: 1,
    very_low: 1,
    low: 2,
    moderate: 3,
    medium: 3,
    high: 4,
    very_high: 5,
  }

  const normalizedLevel = level?.toLowerCase?.() || 'medium'
  const filledDots = levelToDots[normalizedLevel] || 3

  // Get color based on level - using CSS custom properties for theming
  const getColor = () => {
    switch (normalizedLevel) {
      case 'speculative':
      case 'very_low':
        return 'var(--color-danger, #ef4444)'
      case 'low':
        return 'var(--color-warning-light, #f97316)'
      case 'moderate':
      case 'medium':
        return 'var(--color-warning, #f59e0b)'
      case 'high':
        return 'var(--color-success-light, #22c55e)'
      case 'very_high':
        return 'var(--color-success, #10b981)'
      default:
        return 'var(--color-text-muted, #6b7280)'
    }
  }

  // Get background color for the bar (lighter version)
  const getBarBgColor = () => {
    switch (normalizedLevel) {
      case 'speculative':
      case 'very_low':
        return 'rgba(239, 68, 68, 0.12)'
      case 'low':
        return 'rgba(249, 115, 22, 0.12)'
      case 'moderate':
      case 'medium':
        return 'rgba(245, 158, 11, 0.12)'
      case 'high':
        return 'rgba(34, 197, 94, 0.12)'
      case 'very_high':
        return 'rgba(16, 185, 129, 0.12)'
      default:
        return 'var(--bg-tertiary, rgba(128, 128, 128, 0.1))'
    }
  }

  // Format label text
  const getLabelText = () => {
    const labels: Record<string, string> = {
      speculative: 'Speculative',
      very_low: 'Very Low',
      low: 'Low',
      moderate: 'Moderate',
      medium: 'Medium',
      high: 'High',
      very_high: 'Very High',
    }
    return labels[normalizedLevel] || 'Unknown'
  }

  // Get description text for level
  const getLevelDescription = () => {
    switch (normalizedLevel) {
      case 'speculative':
        return 'Very limited data - treat as speculative'
      case 'very_low':
        return 'Limited data available'
      case 'low':
        return 'Some uncertainty in prediction'
      case 'moderate':
      case 'medium':
        return 'Reasonable data quality'
      case 'high':
        return 'Good data coverage'
      case 'very_high':
        return 'Excellent data quality'
      default:
        return ''
    }
  }

  // Get semantic label for accessibility
  const getSemanticLabel = () => {
    return `Confidence: ${Math.round(normalizedScore)}% - ${getLabelText()}. ${getLevelDescription()}`
  }

  // Format flags for display
  const formatFlag = (flag: string) => {
    return flag
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase())
  }

  // Get size-specific classes
  const getSizeClass = () => {
    switch (size) {
      case 'sm':
        return 'confidence-sm'
      case 'lg':
        return 'confidence-lg'
      default:
        return ''
    }
  }

  // Check if there are warnings to highlight
  const hasWarnings = flags.length > 0 || normalizedLevel === 'speculative' || normalizedLevel === 'very_low'

  if (compact) {
    return (
      <div
        className={`confidence-indicator confidence-indicator-compact ${getSizeClass()}`}
        title={getSemanticLabel()}
        role="img"
        aria-label={getSemanticLabel()}
      >
        <div className="confidence-dots" aria-hidden="true">
          {Array.from({ length: 5 }).map((_, i) => (
            <span
              key={i}
              className={`confidence-dot ${i < filledDots ? 'filled' : ''} ${animated ? 'animated' : ''}`}
              style={{
                backgroundColor: i < filledDots ? getColor() : undefined,
                animationDelay: animated ? `${i * 50}ms` : undefined,
              }}
            />
          ))}
        </div>
        <span className="confidence-pct-compact">{Math.round(normalizedScore)}%</span>
      </div>
    )
  }

  // Enhanced bar display mode
  if (showBar) {
    return (
      <div
        className={`confidence-indicator confidence-indicator-bar ${getSizeClass()} ${hasWarnings ? 'has-warnings' : ''}`}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        onFocus={() => setShowTooltip(true)}
        onBlur={() => setShowTooltip(false)}
        role="meter"
        aria-valuenow={Math.round(normalizedScore)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={getSemanticLabel()}
        tabIndex={0}
      >
        <div className="confidence-bar-container">
          <div className="confidence-bar-header">
            <span className="confidence-bar-label">
              {hasWarnings && (
                <IoAlertCircleOutline
                  size={14}
                  className="confidence-warning-icon"
                  style={{ color: getColor() }}
                />
              )}
              {getLabelText()}
              {flags.length > 0 && (
                <IoInformationCircleOutline size={14} className="confidence-info-icon" />
              )}
            </span>
            <span className="confidence-bar-pct" style={{ color: getColor() }}>
              {Math.round(normalizedScore)}%
            </span>
          </div>
          <div className="confidence-bar-track" style={{ backgroundColor: getBarBgColor() }}>
            <div
              className={`confidence-bar-fill ${animated ? 'animated' : ''}`}
              style={{
                width: `${animated ? animatedWidth : normalizedScore}%`,
                backgroundColor: getColor(),
              }}
            />
            {/* Threshold markers */}
            <div className="confidence-bar-marker" style={{ left: '33%' }} title="Low threshold" />
            <div className="confidence-bar-marker" style={{ left: '66%' }} title="High threshold" />
          </div>
          <div className="confidence-bar-labels">
            <span>Low</span>
            <span>Medium</span>
            <span>High</span>
          </div>
        </div>

        {/* Tooltip */}
        {showTooltip && (flags.length > 0 || getLevelDescription()) && (
          <div
            className={`confidence-tooltip confidence-tooltip-${tooltipPosition}`}
            role="tooltip"
          >
            <div className="confidence-tooltip-score">
              <strong>{Math.round(normalizedScore)}%</strong> confidence
            </div>
            <div className="confidence-tooltip-desc">{getLevelDescription()}</div>
            {flags.length > 0 && (
              <div className="confidence-tooltip-flags">
                <span className="confidence-tooltip-flags-label">Data quality notes:</span>
                <ul>
                  {flags.slice(0, 4).map((flag, i) => (
                    <li key={i}>{formatFlag(flag)}</li>
                  ))}
                  {flags.length > 4 && (
                    <li className="more-flags">+{flags.length - 4} more</li>
                  )}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  // Default dots display
  return (
    <div
      className={`confidence-indicator ${getSizeClass()}`}
      role="img"
      aria-label={getSemanticLabel()}
    >
      <div className="confidence-dots" aria-hidden="true">
        {Array.from({ length: 5 }).map((_, i) => (
          <span
            key={i}
            className={`confidence-dot ${i < filledDots ? 'filled' : ''} ${animated ? 'animated' : ''}`}
            style={{
              backgroundColor: i < filledDots ? getColor() : undefined,
              animationDelay: animated ? `${i * 50}ms` : undefined,
            }}
          />
        ))}
      </div>
      {showLabel && (
        <span className="confidence-label" style={{ color: getColor() }}>
          {getLabelText()}
        </span>
      )}
      <span className="confidence-pct">{Math.round(normalizedScore)}%</span>
    </div>
  )
}

// Export memoized version for performance optimization
export const ConfidenceIndicator = memo(ConfidenceIndicatorBase, (prev, next) => {
  return (
    prev.score === next.score &&
    prev.level === next.level &&
    prev.compact === next.compact &&
    prev.showBar === next.showBar &&
    prev.showLabel === next.showLabel &&
    prev.size === next.size &&
    prev.animated === next.animated &&
    JSON.stringify(prev.flags) === JSON.stringify(next.flags)
  )
})
